import { useMemo, useRef, useState } from 'react'

import { useMediaReviewTable } from '~/modules/retailMedia/components/_tables/MediaReviewTable/hooks/useMediaReviewTable'
import { useReviewAdActions } from '~/modules/retailMedia/hooks/useReviewAdActions'

export const useReviewMediaQueue = ({
  initialItemId,
  onClose
}: {
  initialItemId: string
  onClose: () => void
}) => {
  const { data: items } = useMediaReviewTable()

  const [currentId, setCurrentId] = useState(initialItemId)
  const [mode, setMode] = useState<ReviewMediaMode>('view')
  const [animateTransition, setAnimateTransition] = useState(false)
  const [reviewedIds, setReviewedIds] = useState(() => new Set<string>())
  const currentIdRef = useRef(currentId)
  const onCloseRef = useRef(onClose)

  currentIdRef.current = currentId
  onCloseRef.current = onClose

  const displayItems = useMemo(
    () => (items ?? []).filter(item => !reviewedIds.has(item.id)),
    [items, reviewedIds]
  )
  const displayItemsRef = useRef(displayItems)
  displayItemsRef.current = displayItems

  const currentIndex = useMemo(
    () => displayItems.findIndex(item => item.id === currentId),
    [displayItems, currentId]
  )

  const currentItem = currentIndex >= 0 ? displayItems[currentIndex] : null

  const handleResult = () => {
    const reviewedId = currentIdRef.current
    const queue = displayItemsRef.current
    const reviewedIndex = queue.findIndex(item => item.id === reviewedId)
    const remaining = queue.filter(item => item.id !== reviewedId)

    setReviewedIds(current => new Set(current).add(reviewedId))
    setMode('view')
    setAnimateTransition(false)

    if (remaining.length === 0) {
      onCloseRef.current()
      return
    }

    const nextIndex = Math.min(Math.max(reviewedIndex, 0), remaining.length - 1)
    setCurrentId(remaining[nextIndex].id)
  }

  const { approveBanner, rejectBanner, handleApprove, handleReject } =
    useReviewAdActions({
      adId: currentId,
      onApproveSuccess: handleResult,
      onRejectSuccess: handleResult
    })

  const handleGoNext = () => {
    if (displayItems.length < 2) {
      return
    }

    const nextIndex = (currentIndex + 1) % displayItems.length
    setAnimateTransition(true)
    setCurrentId(displayItems[nextIndex].id)
    setMode('view')
  }

  const handleGoPrev = () => {
    if (displayItems.length < 2) {
      return
    }

    const prevIndex =
      (currentIndex - 1 + displayItems.length) % displayItems.length
    setAnimateTransition(true)
    setCurrentId(displayItems[prevIndex].id)
    setMode('view')
  }

  return {
    currentId,
    currentItem,
    currentIndex,
    displayItems,
    mode,
    setMode,
    animateTransition,
    approveBanner,
    rejectBanner,
    handleApprove,
    handleReject,
    handleGoNext,
    handleGoPrev
  }
}
